import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import { InvSelect } from '../../components/InvField';
import { getDispenses } from '../../api/dispensing';
import { exportToCsv, formatDate } from '../../utils';
import { canAllocateDispense, canDeliverDispense } from '../../utils/pharmacyDispenseFlow';
import { dedupeDispensesByOrder, duplicateDispenseCount } from '../../utils/dispenseOrderLink';
import { IPD_COLORS } from '../../theme';
import { fadeInSx } from './pharmacyStyles';
import { PharmacyFilterBar } from './PharmacyUi';

export default function DispenseList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const base = url.replace('/dispenses', '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = {};
      if (statusFilter) p.status = statusFilter;
      const data = await getDispenses(p);
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // One order can own several open transactions — every "Continue Dispense" used to
  // create another. Show only the live one per order so the list reads as work, not
  // as a history of restarts. Nothing is deleted; the hidden rows still open at
  // /dispenses/<id>.
  const rows = useMemo(() => dedupeDispensesByOrder(items), [items]);
  const hiddenDuplicates = useMemo(() => duplicateDispenseCount(items), [items]);
  const reservedCount = rows.filter((i) => i.status === 'RESERVED').length;

  const columns = [
    { field: 'transaction_number', headerName: 'Dispense #', width: 160 },
    { field: 'patient_id', headerName: 'Patient', width: 130 },
    { field: 'doctor_id', headerName: 'Doctor', width: 120 },
    { field: 'status', headerName: 'Status', width: 130, renderCell: (p) => <StatusChip status={p.value} /> },
    {
      field: 'dispense_stage',
      headerName: 'Stage',
      width: 120,
      renderCell: (p) => (p.value ? <StatusChip status={p.value} /> : null),
    },
    {
      field: 'billing_status',
      headerName: 'Billing',
      width: 120,
      renderCell: (p) => (p.value ? <StatusChip status={p.value} /> : null),
    },
    { field: 'transaction_date', headerName: 'Date', width: 120, valueFormatter: (p) => formatDate(p.value) },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (p) => {
        const id = p.row.transaction_id || p.row.id;
        const needsAction = canAllocateDispense(p.row) || canDeliverDispense(p.row);
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => history.push(`${url}/${id}`)}
                sx={{ color: IPD_COLORS.primary, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.1)' } }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {needsAction && (
              <Tooltip title={canAllocateDispense(p.row) ? 'Allocate' : 'Deliver'}>
                <IconButton
                  size="small"
                  onClick={() => history.push(`${url}/${id}`)}
                  sx={{ color: IPD_COLORS.success.text, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.1)' } }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={fadeInSx}>
      <PageHeader
        eyebrow="Pharmacy Management"
        title="Dispenses"
        description={`${rows.length} records${reservedCount > 0 ? ` · ${reservedCount} reserved` : ''}${hiddenDuplicates > 0 ? ` · ${hiddenDuplicates} superseded attempt(s) hidden` : ''}`}
        badge={reservedCount > 0 ? `${reservedCount} reserved` : undefined}
        actions={
          <>
            <InvButton
              startIcon={<DownloadIcon />}
              variant="outlined"
              tone="teal"
              size="small"
              onClick={() => exportToCsv('dispenses.csv', rows)}
            >
              Export
            </InvButton>
            <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${base}/dispense/new`)}>
              New Dispense
            </InvButton>
          </>
        }
      />

      <PharmacyFilterBar>
        <Box sx={{ width: 220 }}>
          <InvSelect
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              ...['DRAFT', 'RESERVED', 'DISPENSED', 'CANCELLED'].map((s) => ({ value: s, label: s })),
            ]}
          />
        </Box>
      </PharmacyFilterBar>

      <InvDataGrid
        fillHeight
        rows={rows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={(r) => r.transaction_id || r.id}
        emptyMessage="No dispenses found."
        emptyAction={(
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${base}/dispense/new`)}>
            New Dispense
          </InvButton>
        )}
        onRowClick={(p) => history.push(`${url}/${p.row.transaction_id || p.row.id}`)}
      />
    </Box>
  );
}
